'use client';
import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { getLocal, removeLocal, setLocal } from '@/lib/storage';

interface AuthState {
  isAuthenticated: boolean;
  accountId: string | null;
  phoneNumber: string | null;
  sessionId: string | null;
}

interface AuthContextType extends AuthState {
  login: (accountId: string, phoneNumber: string, sessionId: string) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface StoredAuthState {
  accountId: string | null;
  phoneNumber: string | null;
  sessionId: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    accountId: null,
    phoneNumber: null,
    sessionId: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const posthog = usePostHog();

  // Load auth state from storage on mount
  useEffect(() => {
    const parsedAuth = getLocal<StoredAuthState>('auth', {
      accountId: null,
      phoneNumber: null,
      sessionId: null,
    });
    const newState = {
      isAuthenticated: Boolean(parsedAuth.sessionId),
      sessionId: parsedAuth.sessionId,
      accountId: parsedAuth.accountId,
      phoneNumber: parsedAuth.phoneNumber,
    };
    setAuthState(newState);
    setIsLoading(false);
  }, []);

  // Update storage when auth state changes
  const updateStorage = (newState: Partial<AuthState>) => {
    const storedAuth: StoredAuthState = {
      accountId: newState.accountId ?? authState.accountId,
      phoneNumber: newState.phoneNumber ?? authState.phoneNumber,
      sessionId: newState.sessionId ?? authState.sessionId,
    };
    setLocal('auth', storedAuth);
  };

  const login = (accountId: string, phoneNumber: string, sessionId: string) => {
    const newState = {
      isAuthenticated: true,
      accountId,
      phoneNumber,
      sessionId,
    };
    setAuthState(newState);
    updateStorage(newState);
  };

  const logout = async () => {
    posthog.reset();
    setLocal('auth', { accountId: null, phoneNumber: null, sessionId: null });
    removeLocal('instacartAuth');
    setAuthState({
      isAuthenticated: false,
      accountId: null,
      phoneNumber: null,
      sessionId: null,
    });
    await Promise.all([
      router.replace('/login'),
      router.refresh()
    ]);
  };

  if (isLoading) {
    return null;
  }

  return (
    <AuthContext.Provider 
      value={{ 
        ...authState, 
        login, 
        logout, 
        isLoading 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 